import React, { useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Header } from '../components/Layout';
import { T, useText } from '../components/Text';
import { useAuth } from '../store/Auth';
import { usePage } from '../hooks/useResource';
import Api from '../services/api';

/**
 * Giriş Sayfası (/login, /login.html)
 * Şube seçimi (Çarşı / Kent) ve e-posta/şifre ile kimlik doğrulama sağlar.
 */
export default function Login() {
  const { user } = useAuth();
  const { t } = useText();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // Şube seçimi (varsayılan: Çarşı veya son seçilen şube)
  const [selectedBranch, setSelectedBranch] = useState(
    localStorage.getItem('selectedBranch') || 'UIhoz0LQo077vwperTau'
  );
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  usePage(t('Giriş'), 'login-page');

  // Giriş sonrası gidilecek sayfayı hesapla
  const getDestination = (account) => {
    const requested = params.get('next');
    const managerRoutes = [
      '/admin',
      '/orders',
      '/order-create',
      '/orders/create',
      '/customers',
      '/settings',
      '/account',
      '/admin.html',
      '/orders.html',
      '/order-create.html',
      '/customers.html',
      '/settings.html',
      '/account.html',
    ];
    const customerRoutes = [
      '/customer',
      '/orders',
      '/account',
      '/customer.html',
      '/orders.html',
      '/account.html',
    ];
    const allowed = Api.isManager(account) ? managerRoutes : customerRoutes;
    if (requested && allowed.includes(requested)) {
      return requested.replace('.html', '');
    }
    return Api.home(account);
  };

  if (user) {
    return <Navigate to={getDestination(user)} replace />;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (busy) return;

    if (!selectedBranch) {
      setError(t('Lütfen bir şube seçin.'));
      return;
    }

    setBusy(true);
    setError('');

    try {
      const data = await Api.mutate('/api/login', 'POST', {
        username: username.trim(),
        password,
        branch: selectedBranch,
      });
      Api.csrfToken = data.csrfToken;
      Api.setUser(data.user);
      navigate(getDestination(data.user), { replace: true });
    } catch (failure) {
      setError(failure.message || 'Giriş yapılamadı. Lütfen bilgilerinizi kontrol edin.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Header login />
      <main className="login-layout">
        {/* Sol Panel: Görsel ve Marka Vurgusu */}
        <div className="login-image">
          <img src="/assets/casting.jpg" alt={t('Zirve Döküm atölyesinde üretim')} />
          <div>
            <span className="eyebrow">
              <T>ZİRVE DÖKÜM</T>
            </span>
            <h2>
              <T>Üretim bizde.</T>
              <br />
              <em>
                <T>Takibi sizde.</T>
              </em>
            </h2>
          </div>
        </div>

        {/* Sağ Panel: Giriş Kartı */}
        <section className="login-card" aria-labelledby="login-title">
          <div className="eyebrow">
            <T>HESABINIZA GİRİŞ</T>
          </div>
          <h1 id="login-title">
            <T>Tekrar hoş geldiniz.</T>
          </h1>
          <p>
            <T>Siparişlerinize ulaşmak için şubenizi seçin ve giriş yapın.</T>
          </p>

          <form id="login-form" onSubmit={handleSubmit}>
            {/* Şube Seçimi */}
            <label htmlFor="login-branch-select">
              <T>Şube Seçimi</T>
            </label>
            <select
              id="login-branch-select"
              name="branch"
              value={selectedBranch}
              onChange={(e) => {
                setSelectedBranch(e.target.value);
                localStorage.setItem('selectedBranch', e.target.value);
              }}
              required
              style={{
                width: '100%',
                padding: '12px 14px',
                marginBottom: '16px',
                background: 'var(--color-bg-card, #1a1c18)',
                color: 'var(--color-text-main, #f5f3ec)',
                border: '1px solid var(--color-border, #3a3830)',
                borderRadius: '8px',
                fontSize: '15px',
                cursor: 'pointer',
              }}
            >
              <option value="UIhoz0LQo077vwperTau">{t('Çarşı Şubesi')}</option>
              <option value="vvG8ruigVsJChISbvnNM">{t('Kent Şubesi (Kuyumcukent)')}</option>
            </select>

            {/* E-posta veya Kullanıcı Adı */}
            <label htmlFor="username">
              <T>E-posta Adresi veya Kullanıcı Adı</T>
            </label>
            <input
              id="username"
              name="username"
              autoComplete="username"
              required
              maxLength={120}
              placeholder={t('E-posta adresiniz')}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />

            {/* Şifre */}
            <label htmlFor="password">
              <T>Şifre</T>
            </label>
            <div className="password-input">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                maxLength={200}
                placeholder={t('Şifreniz')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                id="toggle-password"
                type="button"
                aria-label={t(showPassword ? 'Şifreyi gizle' : 'Şifreyi göster')}
                aria-pressed={showPassword}
                onClick={() => setShowPassword((prev) => !prev)}
              >
                <T>{showPassword ? 'Gizle' : 'Göster'}</T>
              </button>
            </div>

            {error && (
              <p id="login-error" className="form-error" role="alert">
                <T>{error}</T>
              </p>
            )}

            <button className="button gold" id="login-submit" type="submit" disabled={busy}>
              <T>{busy ? 'Giriş Yapılıyor…' : 'Giriş Yap '}</T>
              <span aria-hidden="true">↗</span>
            </button>
          </form>

          <span className="login-footnote">
            <T>Hesabınızla ilgili yardım için </T>
            <a
              href="https://wa.me/905385043881?text=Merhaba%2C%20Zirve%20D%C3%B6k%C3%BCm%20sitesindeki%20hesab%C4%B1mla%20ilgili%20yard%C4%B1m%20almak%20istiyorum."
              target="_blank"
              rel="noopener noreferrer"
            >
              <T>bize mesaj yazın ↗</T>
            </a>
          </span>
        </section>
      </main>
      <p className="site-credit">By Serdar Sunman ● Sedrman</p>
    </>
  );
}
